/* Minimal GA4 helper for Vite + React */

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

const GA_ID = (import.meta as any).env?.VITE_GA_MEASUREMENT_ID as
  | string
  | undefined;

let initialized = false;

function ensureDataLayer() {
  if (!('dataLayer' in window)) {
    (window as any).dataLayer = [];
  }
}

function injectScript(id: string) {
  const existing = document.querySelector(
    `script[src^="https://www.googletagmanager.com/gtag/js?id="]`
  );
  if (existing) return;
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
    id
  )}`;
  document.head.appendChild(script);
}

export function initAnalytics() {
  if (initialized) return;
  if (!GA_ID) {
    if (typeof window !== 'undefined') {
      // Expose quick debug flags in dev
      (window as any).__GA_ENABLED__ = false;
      (window as any).__GA_ID__ = undefined;
      console.info('[analytics] GA4 disabled: missing VITE_GA_MEASUREMENT_ID');
    }
    return;
  }
  ensureDataLayer();

  // Basic gtag shim
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments as unknown as any);
  } as any;

  // Consent Mode v2 - default granted for now
  // You can switch to denied or granular with updateConsent()
  window.gtag('consent', 'default', {
    ad_storage: 'granted',
    analytics_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
    wait_for_update: 500,
  });

  injectScript(GA_ID);

  // Initialize GA4 and disable automatic page_view so we control it explicitly
  window.gtag('js', new Date());
  window.gtag('config', GA_ID, { send_page_view: false });
  initialized = true;
  (window as any).__GA_ENABLED__ = true;
  (window as any).__GA_ID__ = GA_ID;
}

export const analyticsEnabled = Boolean(GA_ID);

export function trackPageview(path?: string) {
  if (!GA_ID || !window.gtag) return;
  window.gtag('event', 'page_view', {
    page_title: document.title,
    page_location: window.location.href,
    page_path: path ?? window.location.pathname,
  });
}

export function trackEvent(name: string, params?: Record<string, any>) {
  if (!GA_ID || !window.gtag) return;
  window.gtag('event', name, params ?? {});
}

// Update consent at runtime (e.g., from a banner)
export function updateConsent(options: {
  ad_storage?: 'granted' | 'denied';
  analytics_storage?: 'granted' | 'denied';
  ad_user_data?: 'granted' | 'denied';
  ad_personalization?: 'granted' | 'denied';
}) {
  if (!window.gtag) return;
  window.gtag('consent', 'update', options);
}

// Recommended GA4 event for lead-gen forms
export function trackLead(params?: {
  form_id?: string;
  form_name?: string;
  value?: number;
  currency?: string; // e.g., 'USD'
}) {
  trackEvent('generate_lead', params);
}

// Handy helpers for common UI events
export function trackCtaClick(params?: { id?: string; text?: string; href?: string; location?: string }) {
  trackEvent('select_content', { content_type: 'cta', ...params });
}

export function trackOutboundClick(params: { href: string; text?: string }) {
  trackEvent('click', { link_domain: new URL(params.href).hostname, link_url: params.href, link_text: params.text });
}
